import { inject } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import { UserContext } from '../../contexts/user-context';
import { UserApi } from '../../apis/user/user-api';
import { PasskeyApi } from '../../apis/passkey/passkey-api';
import { prepareCreationOptions, serializeAttestationCredential } from '../../infrastructure/webauthn-helpers';
import { LanguageHelper } from '../../infrastructure/language-helper';

@inject(UserContext, UserApi, PasskeyApi, I18N)
export class AccountPage {
    public showDeleteConfirm: boolean = false;
    public isBusy: boolean = false;
    public passkeyMessage: string | null = null;
    public passkeyError: string | null = null;

    constructor(
        private readonly _userContext: UserContext,
        private readonly _userApi: UserApi,
        private readonly _passkeyApi: PasskeyApi,
        private readonly _i18n: I18N,
    ) {}

    public get email(): string {
        return this._userContext.userName;
    }

    public get emailMarketingConsent(): boolean {
        return this._userContext.userEntity.emailMarketingConsent;
    }

    public get currentLanguage(): string {
        return LanguageHelper.resolveCurrentLanguage(window.location.pathname, document.cookie);
    }

    public async loading(): Promise<void> {
        if (!this._userContext.isLoggedIn) {
            try {
                const user = await this._userApi.getCurrentUser();
                if (user?.id) this._userContext.setUserInfo(user);
            } catch { /* ignore */ }
        }
    }

    public async toggleEmailConsent(): Promise<void> {
        this.isBusy = true;
        try {
            const updated = await this._userApi.patchConsent(
                this._userContext.userId,
                {
                    emailMarketingConsent: !this.emailMarketingConsent,
                    concurrency: this._userContext.userEntity.concurrency,
                },
            );
            this._userContext.setUserInfo(updated);
        } finally {
            this.isBusy = false;
        }
    }

    public get linkedProviders(): string[] {
        return this._userContext.userEntity.linkedProviders;
    }

    public isProviderLinked(provider: string): boolean {
        const normalizedProvider = this.normalizeProviderName(provider);
        return this.linkedProviders.some(p => this.normalizeProviderName(p) === normalizedProvider);
    }

    private normalizeProviderName(provider: string): string {
        const normalized = (provider ?? '').trim().toLowerCase();
        if (normalized === 'microsoftaccount') {
            return 'microsoft';
        }

        return normalized;
    }

    public async linkProvider(provider: string): Promise<void> {
        this.isBusy = true;
        try {
            const token = await this._userApi.beginLinkProvider();
            sessionStorage.setItem('linkToken', token);
            window.location.href = `/auth/signin/${provider}?returnUrl=${encodeURIComponent(window.location.href)}`;
        } catch {
            this.isBusy = false;
        }
    }

    public setLanguage(language: string): void {
        const normalized = (language ?? '').trim().toLowerCase();
        if (normalized !== 'sv' && normalized !== 'en') {
            return;
        }

        const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
        document.cookie = `SelectedLanguage=${normalized}; expires=${expires}; path=/; SameSite=Lax`;

        const currentPath = window.location.pathname;
        const nextPath = currentPath.replace(/^\/(sv|en)(?=\/|$)/, `/${normalized}`);
        const target = nextPath === currentPath
            ? `/${normalized}${currentPath === '/' ? '' : currentPath}`
            : nextPath;

        window.location.assign(target);
    }

    public signOut(): void {
        window.location.href = '/auth/signout';
    }

    public get passkeyCount(): number {
        return this._userContext.userEntity.passkeyCount;
    }

    public async registerPasskey(): Promise<void> {
        this.passkeyMessage = null;
        this.passkeyError = null;
        this.isBusy = true;
        try {
            const { options, cacheKey } = await this._passkeyApi.registerBegin();
            const creationOptions = prepareCreationOptions(options);
            const credential = await navigator.credentials.create({ publicKey: creationOptions }) as PublicKeyCredential | null;
            if (!credential) { return; }
            const attestation = serializeAttestationCredential(credential);
            await this._passkeyApi.registerComplete({ cacheKey, attestationResponse: JSON.stringify(attestation) });
            this._userContext.userEntity.passkeyCount += 1;
            this.passkeyMessage = this._i18n.tr('ui:account_passkey_registered');
        } catch (e: any) {
            if ((e as DOMException)?.name === 'NotAllowedError') { return; }
            this.passkeyError = e?.content?.title ?? (typeof e?.content === 'string' ? e.content : null) ?? e?.message ?? String(e);
        } finally {
            this.isBusy = false;
        }
    }

    public confirmDelete(): void {
        this.showDeleteConfirm = true;
    }

    public cancelDelete(): void {
        this.showDeleteConfirm = false;
    }

    public async deleteAccount(): Promise<void> {
        this.isBusy = true;
        try {
            await this._userApi.deleteAccount(this._userContext.userId);
            window.location.href = '/auth/signout';
        } catch {
            this.isBusy = false;
            this.showDeleteConfirm = false;
        }
    }

    public openCookieSettings(): void {
        window.dispatchEvent(new Event('sammandrag:open-cookie-settings'));
    }
}
